import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import { Mail } from "@mui/icons-material";
import { StudentListItem } from "../../../../types/StudentModel";

interface SendEmailDialogProps {
  open: boolean;
  onClose: () => void;
  students: StudentListItem[];
  onSend: (subject: string, message: string) => void;
  title?: string;
}

const SendEmailDialog: React.FC<SendEmailDialogProps> = ({
  open,
  onClose,
  students,
  onSend,
  title = "Send Enrollment Email",
}) => {
  const [subject, setSubject] = useState("Your Course Enrollment Confirmation");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (students.length > 0 && open) {
      if (students.length === 1) {
        // Single student email template
        setMessage(
          `Dear ${students[0].name},\n\nWe're pleased to confirm your enrollment in our course. Your account has been successfully activated, and you can now access all course materials.\n\nIf you have any questions, please don't hesitate to contact us.\n\nBest regards,\nThe Education Team`
        );
      } else {
        // Multiple students email template
        setMessage(
          `Dear Students,\n\nWe're pleased to confirm your enrollment in our course. Your accounts have been successfully activated, and you can now access all course materials.\n\nIf you have any questions, please don't hesitate to contact us.\n\nBest regards,\nThe Education Team`
        );
      }
    }
  }, [students, open]);

  const handleSend = () => {
    onSend(subject, message);
    onClose();
  };

  const recipientText =
    students.length === 1
      ? students[0].name
      : `${students.length} selected students`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Send an enrollment confirmation email to {recipientText}.
          {students.length > 1 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2">Recipients:</Typography>
              <Box sx={{ maxHeight: "100px", overflow: "auto", pl: 2 }}>
                {students.map((student) => (
                  <Typography key={student.id} variant="body2" component="div">
                    • {student.name}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </DialogContentText>
        <TextField
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Message"
          multiline
          rows={8}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          startIcon={<Mail />}
        >
          Send Email
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendEmailDialog;
